import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { create } from 'zustand'
import dayjs from 'dayjs'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import { getLastCompleteDate } from '@/lib/date-utils'
import { computeRoasDatePickerDefaultState } from '@/lib/roas-date-picker-defaults'

// Store Factory: 减少冗余代码
export const createDatePickerStore = (isRoas = false, defaultAllowIncomplete = true, rangeDays = 60) => create((set) => {
  const lastCompleteDate = getLastCompleteDate(isRoas).toString()
  const today = dayjs().format('YYYYMMDD')

  return {
    startDate: dayjs(lastCompleteDate, 'YYYYMMDD')
      .subtract(rangeDays, 'day')
      .format('YYYYMMDD'),
    endDate: defaultAllowIncomplete ? today : lastCompleteDate,
    allowIncomplete: defaultAllowIncomplete,
    updateStartDate: (startDate) =>
      set((s) => (s.startDate === startDate ? s : { startDate })),
    updateEndDate: (endDate) =>
      set((s) => (s.endDate === endDate ? s : { endDate })),
    setAllowIncomplete: (allowIncomplete) => set({ allowIncomplete }),
  }
})

export const useDatePicker = createDatePickerStore(false, true, 60)
export const useWeeklyDatePicker = createDatePickerStore(false, false, 60)
export const useDatePicker2 = createDatePickerStore(true, false, 200)
export const useAdmobDatePickerStore = createDatePickerStore(false, true, 30)

// Helper: 统一解析格式
const parseDateStr = (str) => dayjs(str, 'YYYYMMDD')
const parseInputStr = (str) => dayjs(str, 'YYYY-MM-DD')

/**
 * @param {boolean} [defaultAllowIncomplete] 可选。传入时，进入该页面时会将「显示不完整数据」勾选状态设为该值，用于按页面定制默认行为。
 */
export const MyDatePicker = ({
  className,
  datePickerStore = useDatePicker,
  getMaxDate = () => dayjs(),
  showMaxDate = true,
  defaultAllowIncomplete,
  labels = {
    presets: '最近',
    daysUnit: '天',
    showIncomplete: '显示不完整数据',
    completeUntil: '完整数据截至：',
    pickDate: 'Pick a date',
    cancel: '取消',
    confirm: '确认',
  }
}) => {
  const startDate = datePickerStore((state) => state.startDate)
  const endDate = datePickerStore((state) => state.endDate)
  const allowIncomplete = datePickerStore((state) => state.allowIncomplete)
  const setAllowIncomplete = datePickerStore((state) => state.setAllowIncomplete)
  const updateStartDate = datePickerStore((state) => state.updateStartDate)
  const updateEndDate = datePickerStore((state) => state.updateEndDate)

  const { toast } = useToast()
  const contrastEnabled = useContrastModeStore((state) => state.enabled)

  const maxCompleteDate = useMemo(() => {
    let date
    if (typeof getMaxDate === 'function') date = getMaxDate()
    else if (getMaxDate === 'complete') date = getLastCompleteDate(false)
    else if (getMaxDate === 'roas-complete') date = getLastCompleteDate(true)
    else date = getMaxDate

    if (typeof date === 'number' || (typeof date === 'string' && /^\d{8}$/.test(date))) {
      return dayjs(date.toString(), 'YYYYMMDD')
    }
    return dayjs(date)
  }, [getMaxDate])

  // 当前环境下的真正最大可选日期（如果是对比模式，则不限制；开启不完整数据，则为今日；否则为完整数据日）
  const currentMaxLimit = useMemo(() => {
    if (contrastEnabled) return dayjs().add(10, 'year').endOf('day') // 对比模式允许选择未来日期
    return allowIncomplete ? dayjs().endOf('day') : maxCompleteDate.endOf('day')
  }, [contrastEnabled, allowIncomplete, maxCompleteDate])

  // 按页面设置默认是否勾选「显示不完整数据」：仅当 defaultAllowIncomplete 变化时同步（如切换 isTotal/页面），不随 endDate 变化重复执行以免覆盖用户手动操作。
  // 使用 useLayoutEffect：在浏览器绘制与子组件中依赖该 store 的 useSWR 发起请求前对齐；与 RoasDatePickerTroasGate 共用 computeRoasDatePickerDefaultState。
  useLayoutEffect(() => {
    if (defaultAllowIncomplete === undefined) return
    const completeDateStr = maxCompleteDate.format('YYYYMMDD')
    datePickerStore.setState((s) =>
      computeRoasDatePickerDefaultState(s, {
        defaultAllowIncomplete,
        completeDateStr,
      }),
    )
  }, [defaultAllowIncomplete, maxCompleteDate, datePickerStore])

  const [open, setOpen] = useState(false)
  const [tempDateRange, setTempDateRange] = useState({ 
    from: parseDateStr(startDate).toDate(), 
    to: parseDateStr(endDate).toDate() 
  })
  const [tempDisplayStartDate, setTempDisplayStartDate] = useState(parseDateStr(startDate).format('YYYY-MM-DD'))
  const [tempDisplayEndDate, setTempDisplayEndDate] = useState(parseDateStr(endDate).format('YYYY-MM-DD'))
  const [customDays, setCustomDays] = useState('')

  const prevOpenRef = useRef(open)
  const prevContrastEnabledRef = useRef(contrastEnabled)

  // 对比模式切换保护
  useEffect(() => {
    if (prevContrastEnabledRef.current && !contrastEnabled) {
      if (parseDateStr(endDate).isAfter(maxCompleteDate.endOf('day'))) {
        updateEndDate(maxCompleteDate.format('YYYYMMDD'))
      }
    }
    prevContrastEnabledRef.current = contrastEnabled
  }, [contrastEnabled, endDate, updateEndDate, maxCompleteDate])

  // Popover 同步
  useEffect(() => {
    if (prevOpenRef.current !== open) {
      setTempDateRange({ from: parseDateStr(startDate).toDate(), to: parseDateStr(endDate).toDate() })
      setTempDisplayStartDate(parseDateStr(startDate).format('YYYY-MM-DD'))
      setTempDisplayEndDate(parseDateStr(endDate).format('YYYY-MM-DD'))
      setCustomDays('')
      prevOpenRef.current = open
    }
  }, [open, startDate, endDate])

  const handleAllowIncompleteChange = (checked) => {
    setAllowIncomplete(checked)
    const completeDateStr = maxCompleteDate.format('YYYYMMDD')
    
    if (checked) {
      // 勾选时：如果当前是旧的完整日期，跳到今日；如果是更早的日期，则不动
      if (endDate === completeDateStr) {
        const todayStr = dayjs().format('YYYYMMDD')
        updateEndDate(todayStr)
        setTempDisplayEndDate(dayjs().format('YYYY-MM-DD'))
        setTempDateRange(prev => ({ ...prev, to: dayjs().toDate() }))
      }
    } else {
      // 取消勾选时：如果超出范围，强制拉回
      if (parseDateStr(endDate).isAfter(maxCompleteDate.endOf('day'))) {
        updateEndDate(completeDateStr)
        setTempDisplayEndDate(maxCompleteDate.format('YYYY-MM-DD'))
        setTempDateRange(prev => ({ ...prev, to: maxCompleteDate.toDate() }))
      }
    }
  }

  const handleSelectDate = (date) => {
    if (date) {
      setTempDateRange(date)
      if (date.from) setTempDisplayStartDate(dayjs(date.from).format('YYYY-MM-DD'))
      if (date.to) setTempDisplayEndDate(dayjs(date.to).format('YYYY-MM-DD'))
      setCustomDays('')
    }
  }

  // 快捷键逻辑：感知 allowIncomplete 状态
  const handlePresetClick = (days) => {
    // 如果允许不完整数据，基准点设为今日；否则设为完整数据日
    const anchor = allowIncomplete ? dayjs() : maxCompleteDate
    const end = anchor.startOf('day').toDate()
    const start = anchor.subtract(days - 1, 'day').startOf('day').toDate()

    setTempDateRange({ from: start, to: end })
    setTempDisplayStartDate(dayjs(start).format('YYYY-MM-DD'))
    setTempDisplayEndDate(dayjs(end).format('YYYY-MM-DD'))
    setCustomDays(days.toString())
  }

  const handleCustomDaysChange = (value) => {
    setCustomDays(value)
    const days = parseInt(value)
    if (!isNaN(days) && days > 0) {
      const anchor = allowIncomplete ? dayjs() : maxCompleteDate
      const end = anchor.startOf('day').toDate()
      const start = anchor.subtract(days - 1, 'day').startOf('day').toDate()

      setTempDateRange({ from: start, to: end })
      setTempDisplayStartDate(dayjs(start).format('YYYY-MM-DD'))
      setTempDisplayEndDate(dayjs(end).format('YYYY-MM-DD'))
    }
  }

  const handleConfirm = () => {
    const fromVal = parseInputStr(tempDisplayStartDate)
    const toVal = parseInputStr(tempDisplayEndDate)

    if (!fromVal.isValid() || !toVal.isValid()) {
      toast({ title: '日期格式错误', description: '请输入有效的 YYYY-MM-DD 格式日期。', variant: 'destructive' })
      return
    }

    if (fromVal.isAfter(toVal)) {
      toast({ title: '日期范围错误', description: '开始日期不能晚于结束日期。', variant: 'destructive' })
      return
    }

    // 安全防御：非对比模式下禁止手动输入未来的日期
    const absoluteMax = dayjs().endOf('day')
    if (!contrastEnabled && toVal.isAfter(absoluteMax)) {
      toast({ title: '日期超出范围', description: `当前模式下结束日期不能晚于今日 (${absoluteMax.format('YYYY-MM-DD')})。`, variant: 'destructive' })
      return
    }

    // 逻辑检查：如果未勾选不完整数据，限制在完整数据日
    if (!contrastEnabled && !allowIncomplete && toVal.isAfter(maxCompleteDate.endOf('day'))) {
      toast({ title: '日期超出范围', description: `当前模式下结束日期不能晚于 ${maxCompleteDate.format('YYYY-MM-DD')}。`, variant: 'destructive' })
      return
    }

    updateStartDate(fromVal.format('YYYYMMDD'))
    updateEndDate(toVal.format('YYYYMMDD'))
    setOpen(false)
  }

  const lastCompleteDateStr = maxCompleteDate.format('YYYY-MM-DD')
  const date = { from: parseDateStr(startDate).toDate(), to: parseDateStr(endDate).toDate() }

  // 计算日历显示的默认月份：显示包含结束日期的两个月份
  const calendarDefaultMonth = useMemo(() => {
    const end = tempDateRange?.to || date?.to
    if (!end) return tempDateRange?.from || date?.from
    return dayjs(end).subtract(1, 'month').toDate()
  }, [tempDateRange?.to, date?.to, tempDateRange?.from, date?.from])

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn('w-[300px] justify-start text-left font-normal', !date && 'text-muted-foreground')}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>{format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}</>
              ) : (
                format(date.from, 'LLL dd, y')
              )
            ) : (
              <span>{labels.pickDate}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={calendarDefaultMonth}
            selected={tempDateRange}
            onSelect={handleSelectDate}
            numberOfMonths={2}
            disabled={(date) => dayjs(date).isAfter(currentMaxLimit)}
          />
          <div className="flex flex-col gap-3 p-3 border-t bg-muted/10">
            <div className="grid grid-cols-2 gap-3">
              <Input
                id="startDate"
                value={tempDisplayStartDate}
                onChange={(e) => {
                  setTempDisplayStartDate(e.target.value)
                  if (parseInputStr(e.target.value).isValid()) {
                    setTempDateRange(p => ({ ...p, from: parseInputStr(e.target.value).toDate() }))
                  }
                }}
                placeholder="Start Date"
                className="h-8 text-xs bg-background"
              />
              <Input
                id="endDate"
                value={tempDisplayEndDate}
                onChange={(e) => {
                  setTempDisplayEndDate(e.target.value)
                  if (parseInputStr(e.target.value).isValid()) {
                    setTempDateRange(p => ({ ...p, to: parseInputStr(e.target.value).toDate() }))
                  }
                }}
                placeholder="End Date"
                className="h-8 text-xs bg-background"
              />
            </div>
            
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {[3, 7, 30].map((days) => (
                    <Button
                      key={days}
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs font-normal bg-background"
                      onClick={() => handlePresetClick(days)}
                    >
                      {labels.presets}{days}{labels.daysUnit}
                    </Button>
                  ))}
                  <div className="flex items-center gap-1 ml-1">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{labels.presets}</span>
                    <Input
                      type="number"
                      className="w-10 h-8 px-1 text-center text-xs"
                      value={customDays}
                      onChange={(e) => handleCustomDaysChange(e.target.value)}
                      min="1"
                    />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{labels.daysUnit}</span>
                  </div>
                </div>
                
                {showMaxDate && (
                  <div className="flex items-center gap-2 pl-1">
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="allow-incomplete"
                        checked={allowIncomplete}
                        onCheckedChange={handleAllowIncompleteChange}
                        className="h-3 w-3"
                      />
                      <label htmlFor="allow-incomplete" className="text-[11px] text-muted-foreground/70 cursor-pointer select-none">
                        {labels.showIncomplete}
                      </label>
                    </div>
                    <span className="text-[11px] text-muted-foreground/70 font-normal">
                      {labels.completeUntil}{lastCompleteDateStr}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 h-full py-1">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="h-9 px-4 text-xs">{labels.cancel}</Button>
                <Button size="sm" onClick={handleConfirm} className="h-9 px-4 text-xs">{labels.confirm}</Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default MyDatePicker
